import { useQuery } from '@apollo/client';

import { useParams } from 'js/lib/useRouter';

import type { ProductReview } from 'bundles/reviews-common/types/reviewsSharedTypes';
import { getCourseReviewsBySlug } from 'bundles/reviews/queries/reviewsPageQuery';
import { NotFoundError } from 'bundles/ssr/lib/errors/directives';
import inServerContext from 'bundles/ssr/util/inServerContext';

type ReviewsPageQueryData = {
  XdpV1Resource?: {
    course?: {
      elements?: Array<{
        id: string;
        name: string;
        slug: string;
        xdpMetadata?: unknown;
        fetchProductReviews?: { elements?: ProductReview[] };
      }>;
    };
  };
};

type CourseInfo = {
  id: string;
  name: string;
  slug: string;
  xdpMetadata?: {
    cdpMetadata?: {
      description?: string;
      partners?: Array<{ name: string; logo?: string; squareLogo?: string }>;
      testimonials?: { elements?: unknown[] };
      reviewHighlights?: unknown[];
      topReviews?: unknown[];
      ratings?: {
        averageFiveStarRating: number;
        ratingCount: number;
        commentCount: number;
      };
    };
  };
  fetchProductReviews?: {
    elements?: ProductReview[];
  };
};

type ReviewsPageDataResult = {
  loading: boolean;
  error?: Error;
  notFound: boolean;
  courseInfo: CourseInfo | null | undefined;
  schemaReviews: ProductReview[] | null | undefined;
};

export function useReviewsPageData(): ReviewsPageDataResult {
  const params = useParams();
  const slug = params?.slug;

  const { data, loading, error } = useQuery<ReviewsPageQueryData>(getCourseReviewsBySlug, {
    variables: { slug },
    skip: !slug,
    context: {
      ssrBlocking: true,
    },
  });

  const elements = data?.XdpV1Resource?.course?.elements ?? [];
  const courseInfo = elements[0] as CourseInfo | undefined;
  const schemaReviews = courseInfo?.fetchProductReviews?.elements ?? null;

  const notFound = !loading && !!slug && elements.length === 0;

  if (notFound && inServerContext) {
    throw new NotFoundError();
  }

  return {
    loading: !!slug && loading,
    error: error ?? undefined,
    notFound,
    courseInfo: courseInfo ?? null,
    schemaReviews,
  };
}
